import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Target, Zap, Shield, Settings, ChevronRight, ChevronLeft, CheckCircle, Network, Lock, Globe, Key, Shuffle, Wifi, EyeOff, Monitor } from 'lucide-react';
import { scans as scansApi } from '../../api/client';
import apiClient from '../../api/client';
import { getOnlineAgents } from '../../api/agents';
import CGUModal from '../../components/CGUModal';
import ContractErrorModal from '../../components/ContractErrorModal';
import { LicenseGatedButton } from '../../components/LicenseBlock';
import FreePlanWarning from '../../components/FreePlanWarning';
import UpgradeOverlay from '../../components/UpgradeOverlay';
import useSubscription from '../../hooks/useSubscription';
import { useI18n } from '../../i18n';
import { toast } from '../../utils/toastUtils';
import { useWorkspace } from '../../contexts/WorkspaceContext';

// Network Mode definitions
const networkModes = [
  {
    id: 'direct',
    name: 'Scan direct',
    description: 'Scan depuis l\'IP publique du serveur',
    icon: Globe,
    color: '#4caf50',
    features: ['Configuration simple', 'Aucune configuration requise'],
  },
  {
    id: 'vpn_internal',
    name: 'VPN client-à-site',
    description: 'Accès au réseau interne via VPN',
    icon: Key,
    color: '#2196f3',
    features: ['WireGuard/OpenVPN', 'Pentest interne'],
  },
  {
    id: 'vpn_external',
    name: 'VPN commercial',
    description: 'Routage via fournisseur VPN commercial',
    icon: Wifi,
    color: '#ff9800',
    features: ['Rotation d\'IP', 'Ciblage géographique'],
  },
  {
    id: 'proxy_pool',
    name: 'Pool de proxys',
    description: 'Pool de proxys rotatifs',
    icon: Shuffle,
    color: '#9c27b0',
    features: ['Proxys résidentiels', 'Contournement des limites'],
  },
  {
    id: 'agent',
    name: 'Agent Windows',
    description: 'Scanner via un agent installé sur le réseau interne',
    icon: Monitor,
    color: '#ab47bc',
    features: ['Tunnel WebSocket', 'Pentest réseau interne sans VPN'],
  },
];

const NewScan = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { workspaceId } = useWorkspace();
  const { isFreePlan, canCreateScans } = useSubscription();
  const [currentStep, setCurrentStep] = useState(1);
  const [showCGUModal, setShowCGUModal] = useState(false);
  // Contract error modal state
  const [contractError, setContractError] = useState({
    isOpen: false,
    errorType: null,
    message: '',
    workspaceId: null,
    target: ''
  });
  // AI scan quota exceeded modal state
  const [aiQuotaError, setAiQuotaError] = useState({
    isOpen: false,
    message: '',
    used: 0,
    limit: 0
  });
  // Pending scan data (used when user chooses to skip contract or select one)
  const [pendingScanData, setPendingScanData] = useState(null);
  const [formData, setFormData] = useState({
    target: '',
    scan_type: 'network_discovery',
    ports: 'common',
    custom_ports: '',
    timing: 'normal',
    os_detection: true,
    service_detection: true,
    script_scan: false,
    aggressive: false,
    stealth_mode: 'none',  // none, basic, advanced, paranoid
    exclude_hosts: '',
    max_retries: 3,
    timeout: 300,
    config: {},
    // Network mode settings
    network_mode: 'direct',
    vpn_internal_config_id: null,
    vpn_external_config_id: null,
    proxy_pool_id: null,
    agent_id: null,
  });
  const [loading, setLoading] = useState(false);
  const [directInfo, setDirectInfo] = useState(null);
  const [vpnInternalConfigs, setVpnInternalConfigs] = useState([]);
  const [vpnExternalConfigs, setVpnExternalConfigs] = useState([]);
  const [proxyPools, setProxyPools] = useState([]);
  const [availableAgents, setAvailableAgents] = useState([]);

  // Load network mode configurations
  // Load network mode configurations when workspaceId changes
  useEffect(() => {
    loadNetworkConfigs();
  }, [workspaceId]);

  const loadNetworkConfigs = async () => {
    // Only load network configs if we have a valid workspace ID
    // Otherwise, just load direct mode info which doesn't require workspace
    if (!workspaceId) {
      try {
        const direct = await apiClient.get('/network-modes/direct/info').catch(() => ({ data: null }));
        setDirectInfo(direct?.data || null);
        setVpnInternalConfigs([]);
        setVpnExternalConfigs([]);
        setProxyPools([]);
      } catch {
        // Silently handle - direct info is optional
      }
      return;
    }

    try {
      const wsParam = `workspace_id=${workspaceId}`;
      const [direct, vpnInternal, vpnExternal, pools, agents] = await Promise.all([
        apiClient.get('/network-modes/direct/info').catch(() => ({ data: null })),
        apiClient.get(`/network-modes/vpn-internal/configs?${wsParam}`).catch(() => ({ data: [] })),
        apiClient.get(`/network-modes/vpn-external/configs?${wsParam}`).catch(() => ({ data: [] })),
        apiClient.get(`/network-modes/proxy-pools?${wsParam}`).catch(() => ({ data: [] })),
        getOnlineAgents(workspaceId).catch(() => []),
      ]);
      setDirectInfo(direct?.data || null);
      setVpnInternalConfigs(Array.isArray(vpnInternal?.data) ? vpnInternal.data : []);
      setVpnExternalConfigs(Array.isArray(vpnExternal?.data) ? vpnExternal.data : []);
      setProxyPools(Array.isArray(pools?.data) ? pools.data : []);
      setAvailableAgents(Array.isArray(agents) ? agents : []);
    } catch {
      // Silently handle - API errors are already handled individually by .catch() above
    }
  };

  const steps = [
    { id: 1, title: t('scans.steps.targetType', 'Cible & type'), icon: Target },
    { id: 2, title: t('scans.steps.networkMode', 'Mode réseau'), icon: Network },
    { id: 3, title: t('scans.steps.scanOptions', 'Options'), icon: Settings },
    { id: 4, title: t('scans.steps.advanced', 'Avancée'), icon: Lock },
    { id: 5, title: t('scans.steps.review', 'Vérification'), icon: CheckCircle },
  ];

  const handleNext = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = () => {
    // Afficher le modal CGU avant de lancer le scan
    setShowCGUModal(true);
  };

  const handleCGUAccept = async (overrideOptions = {}) => {
    setLoading(true);

    try {
      // Build config object
      const config = {
        ports: formData.ports === 'custom' ? formData.custom_ports : formData.ports,
        timing: formData.timing,
        os_detection: formData.os_detection,
        service_detection: formData.service_detection,
        script_scan: formData.script_scan,
        aggressive: formData.aggressive,
        stealth_mode: formData.stealth_mode,
        exclude_hosts: formData.exclude_hosts,
        max_retries: formData.max_retries,
        timeout: formData.timeout,
        // Network mode settings
        network_mode: formData.network_mode,
        vpn_internal_config_id: formData.vpn_internal_config_id,
        vpn_external_config_id: formData.vpn_external_config_id,
        proxy_pool_id: formData.proxy_pool_id,
      };

      const scanData = {
        target: formData.target,
        scan_type: formData.scan_type,
        config: { ...config, workspace_id: workspaceId },
        network_mode: formData.network_mode,
        workspace_id: workspaceId,
        // Contract options from override
        skip_contract_check: overrideOptions.skip_contract_check || false,
        contract_id: overrideOptions.contract_id || null,
      };

      const newScan = await scansApi.create(scanData);

      // Configure network mode for the scan if not direct
      if (formData.network_mode !== 'direct') {
        try {
          await apiClient.post(`/network-modes/scan/${newScan.id}/configure`, {
            scan_id: newScan.id,
            mode: formData.network_mode,
            vpn_internal_config_id: formData.vpn_internal_config_id,
            vpn_external_config_id: formData.vpn_external_config_id,
            proxy_pool_id: formData.proxy_pool_id,
          });
        } catch (networkError) {
          console.error('Network mode config error:', networkError);
          // Continue anyway - scan will use direct mode as fallback
        }
      }

      navigate(`/scans/${newScan.id}`);
    } catch (error) {
      const errorDetail = error.response?.data?.detail;

      // Handle AI scan quota exceeded error
      if (errorDetail?.error === 'ai_scan_quota_exceeded') {
        setAiQuotaError({
          isOpen: true,
          message: errorDetail.message,
          used: errorDetail.used || 0,
          limit: errorDetail.limit || 0
        });
      }
      // Handle contract validation errors
      else if (errorDetail?.error === 'contract_required' || errorDetail?.error === 'target_not_in_perimeter') {
        // Save pending scan data for retry
        setPendingScanData({ target: formData.target });
        // Show contract error modal with guidance
        setContractError({
          isOpen: true,
          errorType: errorDetail.error,
          message: errorDetail.message,
          workspaceId: errorDetail.workspace_id,
          target: formData.target
        });
      }
      // Handle 500 Internal Server Error
      else if (error.response?.status === 500) {
        toast.error(t('scans.errors.serverError') || 'Erreur serveur. Veuillez réessayer dans quelques instants.');
      }
      // Handle 400 Bad Request (workspace required, etc.)
      else if (error.response?.status === 400 && errorDetail?.error === 'workspace_required') {
        toast.warning(errorDetail.message || t('scans.errors.workspaceRequired') || 'Veuillez sélectionner un workspace avant de lancer un scan.');
      }
      // Handle other errors
      else {
        let message = '';
        try {
          if (errorDetail && typeof errorDetail === 'object') {
            message = errorDetail.message || errorDetail.detail || JSON.stringify(errorDetail);
          } else if (errorDetail) {
            message = String(errorDetail);
          } else if (error.message) {
            message = error.message;
          } else {
            message = 'Erreur inconnue';
          }
        } catch {
          message = 'Erreur lors de la création du scan';
        }
        toast.error(t('scans.errors.createFailed') || `Échec de la création du scan: ${message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle continue without contract
  const handleContinueWithoutContract = () => {
    setContractError({ isOpen: false, errorType: null, message: '', workspaceId: null, target: '' });
    // Retry scan with skip_contract_check
    handleCGUAccept({ skip_contract_check: true });
  };

  // Handle contract selection
  const handleSelectContract = (contractId) => {
    setContractError({ isOpen: false, errorType: null, message: '', workspaceId: null, target: '' });
    // Retry scan with selected contract
    handleCGUAccept({ contract_id: contractId });
  };

  return (
    <>
    {/* Modal CGU */}
    <CGUModal
      isOpen={showCGUModal}
      onClose={() => setShowCGUModal(false)}
      onAccept={handleCGUAccept}
      scanType="Scan Manuel"
    />

    {/* Modal Contract Error */}
    <ContractErrorModal
      isOpen={contractError.isOpen}
      onClose={() => setContractError({ isOpen: false, errorType: null, message: '', workspaceId: null, target: '' })}
      errorType={contractError.errorType}
      message={contractError.message}
      workspaceId={contractError.workspaceId}
      target={contractError.target}
      onContinueWithoutContract={handleContinueWithoutContract}
      onSelectContract={handleSelectContract}
    />

    {/* Modal AI Quota Exceeded */}
    {aiQuotaError.isOpen && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
        <div className="bg-brand-black-light border border-brand-gray-dark rounded-xl p-6 max-w-lg w-full mx-4 shadow-2xl">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-orange-500/20 rounded-full">
              <Shield className="w-6 h-6 text-orange-400" />
            </div>
            <h2 className="text-xl font-bold text-white">{t('scans.aiQuotaExceeded') || 'Quota de scans IA atteint'}</h2>
          </div>

          <p className="text-gray-300 mb-4">
            {aiQuotaError.message}
          </p>

          <div className="bg-brand-black p-4 rounded-lg mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400">{t('scans.aiScansUsed') || 'Scans IA utilisés'}</span>
              <span className="text-white font-bold">{aiQuotaError.used} / {aiQuotaError.limit}</span>
            </div>
            <div className="w-full bg-brand-gray-dark rounded-full h-2">
              <div
                className="bg-orange-500 h-2 rounded-full"
                style={{ width: '100%' }}
              />
            </div>
          </div>

          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-6">
            <p className="text-blue-400 text-sm">
              <strong>{t('scans.upgradeOptions') || 'Options pour continuer'}:</strong>
            </p>
            <ul className="text-gray-300 text-sm mt-2 space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-blue-400">1.</span>
                {t('scans.callSylink') || 'Appelez le service client SYLink:'} <a href="tel:+33415540000" className="text-brand-green hover:underline font-semibold">+33 4 15 54 00 00</a>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-400">2.</span>
                {t('scans.upgradePlan') || 'Passez à un abonnement supérieur pour des scans IA illimités'}
              </li>
            </ul>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => setAiQuotaError({ isOpen: false, message: '', used: 0, limit: 0 })}
              className="flex-1 px-4 py-2 bg-brand-gray-dark hover:bg-brand-gray-light text-white rounded-lg transition-colors"
            >
              {t('common.close') || 'Fermer'}
            </button>
            <button
              onClick={() => window.location.href = 'https://sylink-pentest.com/pricing'}
              className="flex-1 px-4 py-2 bg-brand-green hover:bg-brand-green-dark text-black font-semibold rounded-lg transition-colors"
            >
              {t('scans.viewPlans') || 'Voir les offres'}
            </button>
          </div>
        </div>
      </div>
    )}

    <div className="min-h-screen bg-brand-black p-6">
      <div className="page-container relative">
      <div className="w-[95%] sm:w-[92%] lg:w-[88%] laptop:w-[85%] 2xl:w-[80%] max-w-[1600px] laptop:max-w-[1700px] 2xl:max-w-[1800px] mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate('/scans')}
            className="p-2 hover:bg-brand-black-lighter rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-400 hover:text-brand-green" />
          </button>
          <div>
            <h1 className="text-4xl font-bold text-gradient-green">{t('scans.newScan')}</h1>
            <p className="text-gray-400 mt-2">{t('scans.configureScan')}</p>
          </div>
        </div>

        {/* Free Plan Blocking Overlay */}
        {!canCreateScans && <UpgradeOverlay feature="aux scans de sécurité" />}

        {/* Free Plan Warning */}
        <FreePlanWarning className="mb-6" />

        {/* Progress Steps */}
        <div className="bg-brand-black-light rounded-xl p-6 border border-brand-green/30">
          <div className="flex items-center justify-between">
            {steps.map((step, idx) => (
              <React.Fragment key={step.id}>
                <div className="flex flex-col items-center min-w-[70px] max-w-[120px]">
                  <div
                    className={`w-12 h-12 rounded-full border-2 flex items-center justify-center transition-all flex-shrink-0 ${
                      currentStep >= step.id
                        ? 'border-brand-green bg-brand-green/20 text-brand-green'
                        : 'border-gray-700 text-gray-600'
                    }`}
                  >
                    <step.icon className="w-6 h-6" />
                  </div>
                  <span className={`text-xs mt-2 text-center leading-tight break-words ${currentStep >= step.id ? 'text-brand-green' : 'text-gray-600'}`}>
                    {step.title}
                  </span>
                </div>
                {idx < steps.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-2 ${currentStep > step.id ? 'bg-brand-green' : 'bg-gray-700'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <div className="bg-brand-black-light rounded-xl p-6 border border-brand-green/30 min-h-[500px]">
          {/* Step 1: Target & Type */}
          {currentStep === 1 && (
            <div className="space-y-6 animate-in fade-in duration-300">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-3">
                  {t('scans.target')} *
                </label>
                <input
                  type="text"
                  value={formData.target}
                  onChange={(e) => setFormData({ ...formData, target: e.target.value })}
                  placeholder={t('scans.placeholders.target', '192.168.1.0/24 ou exemple.com')}
                  className="input-field w-full text-lg"
                  autoFocus
                />
                <p className="text-xs text-gray-500 mt-2">
                  {t('scans.placeholders.targetExamples', 'Exemples : 192.168.1.1, 192.168.1.0/24, exemple.com, 10.0.0.0/8')}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-4">
                  {t('scans.scanType')}
                </label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, scan_type: 'network_discovery' })}
                    className={`p-4 rounded-lg border-2 transition-all text-left h-full min-w-0 ${
                      formData.scan_type === 'network_discovery'
                        ? 'border-brand-green bg-brand-green/10'
                        : 'border-gray-800 hover:border-brand-green/50'
                    }`}
                  >
                    <Zap className={`w-6 h-6 mb-2 flex-shrink-0 ${formData.scan_type === 'network_discovery' ? 'text-brand-green' : 'text-gray-400'}`} />
                    <div className="font-semibold text-white mb-1">{t('scans.networkDiscovery')}</div>
                    <div className="text-xs text-gray-500 break-words whitespace-normal leading-relaxed">{t('scans.networkDiscoveryDesc')}</div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, scan_type: 'vulnerability_scan' })}
                    className={`p-4 rounded-lg border-2 transition-all text-left h-full min-w-0 ${
                      formData.scan_type === 'vulnerability_scan'
                        ? 'border-brand-green bg-brand-green/10'
                        : 'border-gray-800 hover:border-brand-green/50'
                    }`}
                  >
                    <Shield className={`w-6 h-6 mb-2 flex-shrink-0 ${formData.scan_type === 'vulnerability_scan' ? 'text-brand-green' : 'text-gray-400'}`} />
                    <div className="font-semibold text-white mb-1">{t('scans.vulnerabilityScan')}</div>
                    <div className="text-xs text-gray-500 break-words whitespace-normal leading-relaxed">{t('scans.vulnerabilityScanDesc')}</div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, scan_type: 'full_autonomous' })}
                    className={`p-4 rounded-lg border-2 transition-all text-left h-full min-w-0 ${
                      formData.scan_type === 'full_autonomous'
                        ? 'border-brand-green bg-brand-green/10'
                        : 'border-gray-800 hover:border-brand-green/50'
                    }`}
                  >
                    <Target className={`w-6 h-6 mb-2 flex-shrink-0 ${formData.scan_type === 'full_autonomous' ? 'text-brand-green' : 'text-gray-400'}`} />
                    <div className="font-semibold text-white mb-1">{t('scans.fullAutonomous')}</div>
                    <div className="text-xs text-gray-500 break-words whitespace-normal leading-relaxed">{t('scans.fullAutonomousDesc')}</div>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Network Mode */}
          {currentStep === 2 && (
            <div className="space-y-6 animate-in fade-in duration-300">
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-3">
                  <div className="text-blue-500 mt-0.5">
                    <Network className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-blue-400 mb-1">Mode de scan réseau</div>
                    <div className="text-xs text-gray-400">
                      Choisissez comment le trafic du scan sera routé. Pour les réseaux internes, utilisez VPN Client-à-Site.
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {networkModes.map((mode) => {
                  const IconComponent = mode.icon;
                  const isSelected = formData.network_mode === mode.id;

                  return (
                    <button
                      key={mode.id}
                      type="button"
                      onClick={() => setFormData({
                        ...formData,
                        network_mode: mode.id,
                        vpn_internal_config_id: null,
                        vpn_external_config_id: null,
                        proxy_pool_id: null,
                        agent_id: null
                      })}
                      className={`p-4 rounded-lg border-2 transition-all text-left ${
                        isSelected
                          ? 'border-brand-green bg-brand-green/10'
                          : 'border-gray-800 hover:border-brand-green/50'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className="p-2 rounded-lg"
                          style={{ backgroundColor: `${mode.color}20` }}
                        >
                          <IconComponent
                            className="w-5 h-5"
                            style={{ color: mode.color }}
                          />
                        </div>
                        <div className="flex-1">
                          <div className={`font-semibold ${isSelected ? 'text-white' : 'text-gray-300'}`}>
                            {mode.name}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">{mode.description}</div>
                          <div className="flex flex-wrap gap-1 mt-2">
                            {mode.features.map((feature, idx) => (
                              <span
                                key={idx}
                                className="text-xs px-2 py-0.5 rounded"
                                style={{
                                  backgroundColor: `${mode.color}15`,
                                  color: mode.color
                                }}
                              >
                                {feature}
                              </span>
                            ))}
                          </div>
                        </div>
                        {isSelected && (
                          <CheckCircle className="w-5 h-5 text-brand-green" />
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Direct Mode Info */}
              {formData.network_mode === 'direct' && directInfo && (
                <div className="bg-brand-black rounded-lg p-4 border border-gray-800 mt-4">
                  <div className="text-sm text-gray-400 mb-2">Le scan utilisera l'IP publique du serveur :</div>
                  <div className="text-xl font-mono text-brand-green">{directInfo.public_ip}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    {directInfo.city}, {directInfo.country}
                  </div>
                </div>
              )}

              {/* VPN Internal Config Selection */}
              {formData.network_mode === 'vpn_internal' && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-400 mb-3">
                    Sélectionner la configuration VPN
                  </label>
                  {vpnInternalConfigs.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {vpnInternalConfigs.map((config) => (
                        <button
                          key={config.id}
                          type="button"
                          onClick={() => setFormData({ ...formData, vpn_internal_config_id: config.id })}
                          className={`p-3 rounded-lg border text-left transition-all ${
                            formData.vpn_internal_config_id === config.id
                              ? 'border-blue-500 bg-blue-500/10'
                              : 'border-gray-800 hover:border-blue-500/50'
                          }`}
                        >
                          <div className="font-semibold text-white">{config.name}</div>
                          <div className="text-xs text-gray-500">
                            {config.protocol?.toUpperCase()} | {config.server_address}
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 bg-brand-black rounded-lg border border-gray-800">
                      <Key className="w-12 h-12 mx-auto text-gray-600 mb-3" />
                      <div className="text-gray-400">Aucune configuration VPN trouvée</div>
                      <div className="text-xs text-gray-600 mt-1">
                        Configurez le VPN dans Paramètres &rarr; Modes Réseau
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* VPN External Config Selection */}
              {formData.network_mode === 'vpn_external' && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-400 mb-3">
                    Sélectionner le VPN commercial
                  </label>
                  {vpnExternalConfigs.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {vpnExternalConfigs.map((config) => (
                        <button
                          key={config.id}
                          type="button"
                          onClick={() => setFormData({ ...formData, vpn_external_config_id: config.id })}
                          className={`p-3 rounded-lg border text-left transition-all ${
                            formData.vpn_external_config_id === config.id
                              ? 'border-orange-500 bg-orange-500/10'
                              : 'border-gray-800 hover:border-orange-500/50'
                          }`}
                        >
                          <div className="font-semibold text-white">{config.name}</div>
                          <div className="text-xs text-gray-500">
                            {config.provider} | {config.preferred_countries?.join(', ') || 'Toutes les régions'}
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 bg-brand-black rounded-lg border border-gray-800">
                      <Wifi className="w-12 h-12 mx-auto text-gray-600 mb-3" />
                      <div className="text-gray-400">Aucune configuration VPN commercial trouvée</div>
                      <div className="text-xs text-gray-600 mt-1">
                        Configurez les fournisseurs VPN dans Paramètres &rarr; Modes Réseau
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Proxy Pool Selection */}
              {formData.network_mode === 'proxy_pool' && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-400 mb-3">
                    Sélectionner le pool de proxies
                  </label>
                  {proxyPools.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {proxyPools.map((pool) => (
                        <button
                          key={pool.id}
                          type="button"
                          onClick={() => setFormData({ ...formData, proxy_pool_id: pool.id })}
                          className={`p-3 rounded-lg border text-left transition-all ${
                            formData.proxy_pool_id === pool.id
                              ? 'border-purple-500 bg-purple-500/10'
                              : 'border-gray-800 hover:border-purple-500/50'
                          }`}
                        >
                          <div className="font-semibold text-white">{pool.name}</div>
                          <div className="text-xs text-gray-500">
                            {pool.healthy_proxies}/{pool.total_proxies} actifs | {pool.rotation_strategy}
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 bg-brand-black rounded-lg border border-gray-800">
                      <Shuffle className="w-12 h-12 mx-auto text-gray-600 mb-3" />
                      <div className="text-gray-400">Aucun pool de proxies configuré</div>
                      <div className="text-xs text-gray-600 mt-1">
                        Configurez les pools de proxies dans Paramètres &rarr; Modes Réseau
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Agent Selection */}
              {formData.network_mode === 'agent' && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-400 mb-3">
                    Sélectionner l'agent Windows
                  </label>
                  {availableAgents.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {availableAgents.map((agent) => (
                        <button
                          key={agent.id}
                          type="button"
                          onClick={() => setFormData({ ...formData, agent_id: agent.id })}
                          className={`p-3 rounded-lg border text-left transition-all ${
                            formData.agent_id === agent.id
                              ? 'border-purple-500 bg-purple-500/10'
                              : 'border-gray-800 hover:border-purple-500/50'
                          }`}
                        >
                          <div className="font-semibold text-white">{agent.name || agent.hostname}</div>
                          <div className="text-xs text-gray-500">
                            {agent.internal_ip || 'IP inconnue'} | {agent.status}
                            {agent.os_version && ` | ${agent.os_version}`}
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 bg-brand-black rounded-lg border border-gray-800">
                      <Monitor className="w-12 h-12 mx-auto text-gray-600 mb-3" />
                      <div className="text-gray-400">Aucun agent en ligne</div>
                      <div className="text-xs text-gray-600 mt-1">
                        Installez et démarrez un agent Windows sur le réseau cible
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Step 3: Scan Options */}
          {currentStep === 3 && (
            <div className="space-y-6 animate-in fade-in duration-300">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-3">
                  Plage de ports à scanner
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {['common', 'all', 'top1000', 'custom'].map((portOption) => (
                    <button
                      key={portOption}
                      type="button"
                      onClick={() => setFormData({ ...formData, ports: portOption })}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        formData.ports === portOption
                          ? 'border-brand-green bg-brand-green/10 text-white'
                          : 'border-gray-800 hover:border-brand-green/50 text-gray-400'
                      }`}
                    >
                      <div className="font-semibold text-sm capitalize">
                      {portOption === 'common' ? 'Courants' : portOption === 'all' ? 'Tous' : portOption === 'top1000' ? 'Top 1000' : portOption === 'custom' ? 'Personnalisé' : portOption}
                    </div>
                    </button>
                  ))}
                </div>
                {formData.ports === 'custom' && (
                  <input
                    type="text"
                    value={formData.custom_ports}
                    onChange={(e) => setFormData({ ...formData, custom_ports: e.target.value })}
                    placeholder={t('scans.placeholders.customPorts', 'Ex. : 80,443,8080-8090')}
                    className="input-field w-full mt-3"
                  />
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-3">
                  Vitesse du scan
                </label>
                <div className="grid grid-cols-5 gap-2">
                  {[
                    { value: 'paranoid', label: 'Paranoïaque', desc: 'Très lent' },
                    { value: 'sneaky', label: 'Furtif', desc: 'Lent' },
                    { value: 'normal', label: 'Normal', desc: 'Équilibré' },
                    { value: 'aggressive', label: 'Agressif', desc: 'Rapide' },
                    { value: 'insane', label: 'Extrême', desc: 'Très rapide' },
                  ].map((timing) => (
                    <button
                      key={timing.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, timing: timing.value })}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        formData.timing === timing.value
                          ? 'border-brand-green bg-brand-green/10'
                          : 'border-gray-800 hover:border-brand-green/50'
                      }`}
                    >
                      <div className={`font-semibold text-xs ${formData.timing === timing.value ? 'text-white' : 'text-gray-400'}`}>
                        {timing.label}
                      </div>
                      <div className="text-xs text-gray-600 mt-1">{timing.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-brand-black rounded-lg p-4 border border-gray-800">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="os_detection"
                      checked={formData.os_detection}
                      onChange={(e) => setFormData({ ...formData, os_detection: e.target.checked })}
                      className="w-5 h-5 accent-brand-green flex-shrink-0"
                    />
                    <label htmlFor="os_detection" className="text-sm text-gray-300 cursor-pointer flex-1">
                      <div className="font-medium">Détection d'OS</div>
                      <div className="text-xs text-gray-500">Identifier les systèmes d'exploitation</div>
                    </label>
                  </div>
                </div>

                <div className="bg-brand-black rounded-lg p-4 border border-gray-800">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="service_detection"
                      checked={formData.service_detection}
                      onChange={(e) => setFormData({ ...formData, service_detection: e.target.checked })}
                      className="w-5 h-5 accent-brand-green flex-shrink-0"
                    />
                    <label htmlFor="service_detection" className="text-sm text-gray-300 cursor-pointer flex-1">
                      <div className="font-medium">Détection de services</div>
                      <div className="text-xs text-gray-500">Détecter les versions des services</div>
                    </label>
                  </div>
                </div>

                <div className="bg-brand-black rounded-lg p-4 border border-gray-800">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="script_scan"
                      checked={formData.script_scan}
                      onChange={(e) => setFormData({ ...formData, script_scan: e.target.checked })}
                      className="w-5 h-5 accent-brand-green flex-shrink-0"
                    />
                    <label htmlFor="script_scan" className="text-sm text-gray-300 cursor-pointer flex-1">
                      <div className="font-medium">Scan par scripts</div>
                      <div className="text-xs text-gray-500">Exécuter les scripts NSE</div>
                    </label>
                  </div>
                </div>

                <div className="bg-brand-black rounded-lg p-4 border border-gray-800">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="aggressive"
                      checked={formData.aggressive}
                      onChange={(e) => setFormData({ ...formData, aggressive: e.target.checked })}
                      className="w-5 h-5 accent-brand-green flex-shrink-0"
                    />
                    <label htmlFor="aggressive" className="text-sm text-gray-300 cursor-pointer flex-1">
                      <div className="font-medium">Mode agressif</div>
                      <div className="text-xs text-gray-500">Activer toutes les fonctionnalités de détection</div>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Advanced Options */}
          {currentStep === 4 && (
            <div className="space-y-6 animate-in fade-in duration-300">
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="text-yellow-500 mt-0.5">⚠️</div>
                  <div className="flex-1">
                    <div className="font-semibold text-yellow-500 mb-1">Options avancées</div>
                    <div className="text-xs text-gray-400">
                      Ces options peuvent impacter les performances et la détectabilité du scan. Utilisez avec précaution.
                    </div>
                  </div>
                </div>
              </div>

              {/* Stealth Mode Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-3 flex items-center gap-2">
                  <EyeOff className="w-4 h-4" />
                  {t('scans.stealthMode.title') || 'Mode furtif'}
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    {
                      value: 'none',
                      label: t('scans.stealthMode.none') || 'Aucun',
                      desc: t('scans.stealthMode.noneDesc') || 'Scan rapide, détectable',
                      color: 'gray',
                      icon: '⚡'
                    },
                    {
                      value: 'basic',
                      label: t('scans.stealthMode.basic') || 'Basique',
                      desc: t('scans.stealthMode.basicDesc') || 'Paquets fragmentés',
                      color: 'blue',
                      icon: '🔒'
                    },
                    {
                      value: 'advanced',
                      label: t('scans.stealthMode.advanced') || 'Avancé',
                      desc: t('scans.stealthMode.advancedDesc') || 'Timing lent + spoofing',
                      color: 'orange',
                      icon: '🛡️'
                    },
                    {
                      value: 'paranoid',
                      label: t('scans.stealthMode.paranoid') || 'Paranoïaque',
                      desc: t('scans.stealthMode.paranoidDesc') || 'Évasion max (très lent)',
                      color: 'red',
                      icon: '👻'
                    },
                  ].map((mode) => (
                    <button
                      key={mode.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, stealth_mode: mode.value })}
                      className={`p-4 rounded-lg border-2 transition-all text-left ${
                        formData.stealth_mode === mode.value
                          ? `border-${mode.color}-500 bg-${mode.color}-500/10`
                          : 'border-gray-800 hover:border-gray-600'
                      }`}
                      style={{
                        borderColor: formData.stealth_mode === mode.value
                          ? (mode.color === 'gray' ? '#6b7280' : mode.color === 'blue' ? '#3b82f6' : mode.color === 'orange' ? '#f97316' : '#ef4444')
                          : undefined,
                        backgroundColor: formData.stealth_mode === mode.value
                          ? (mode.color === 'gray' ? 'rgba(107, 114, 128, 0.1)' : mode.color === 'blue' ? 'rgba(59, 130, 246, 0.1)' : mode.color === 'orange' ? 'rgba(249, 115, 22, 0.1)' : 'rgba(239, 68, 68, 0.1)')
                          : undefined
                      }}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-lg">{mode.icon}</span>
                        <span className={`font-semibold ${formData.stealth_mode === mode.value ? 'text-white' : 'text-gray-400'}`}>
                          {mode.label}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500">{mode.desc}</div>
                    </button>
                  ))}
                </div>
                {formData.stealth_mode !== 'none' && (
                  <div className="mt-3 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                    <div className="flex items-start gap-2">
                      <span className="text-yellow-500">⏱️</span>
                      <div className="text-xs text-gray-400">
                        {formData.stealth_mode === 'basic' && (t('scans.stealthMode.basicTime') || 'Le scan prendra environ 2x plus de temps')}
                        {formData.stealth_mode === 'advanced' && (t('scans.stealthMode.advancedTime') || 'Le scan prendra environ 5x plus de temps')}
                        {formData.stealth_mode === 'paranoid' && (t('scans.stealthMode.paranoidTime') || 'Le scan prendra environ 10x plus de temps')}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-3">
                  Exclure des hôtes (optionnel)
                </label>
                <input
                  type="text"
                  value={formData.exclude_hosts}
                  onChange={(e) => setFormData({ ...formData, exclude_hosts: e.target.value })}
                  placeholder={t('scans.placeholders.excludeHosts', 'Ex. : 192.168.1.1,192.168.1.5')}
                  className="input-field w-full"
                />
                <p className="text-xs text-gray-500 mt-2">
                  Liste d'hôtes à exclure du scan, séparés par des virgules
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-3">
                    Tentatives max
                  </label>
                  <input
                    type="number"
                    value={formData.max_retries}
                    onChange={(e) => setFormData({ ...formData, max_retries: parseInt(e.target.value) })}
                    min="0"
                    max="10"
                    className="input-field w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-3">
                    Délai d'expiration (secondes)
                  </label>
                  <input
                    type="number"
                    value={formData.timeout}
                    onChange={(e) => setFormData({ ...formData, timeout: parseInt(e.target.value) })}
                    min="60"
                    max="3600"
                    className="input-field w-full"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 5: Review */}
          {currentStep === 5 && (
            <div className="space-y-6 animate-in fade-in duration-300">
              <div className="bg-brand-black rounded-lg p-6 border border-brand-green/20">
                <h3 className="text-xl font-bold text-brand-green mb-4">Résumé de la configuration du scan</h3>
                
                <div className="space-y-4">
                  {/* Basic Info */}
                  <div>
                    <h4 className="text-sm font-bold text-gray-400 mb-3">Configuration de base</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="text-xs text-gray-500 block mb-1">Cible</span>
                        <span className="text-white font-mono">{formData.target}</span>
                      </div>
                      <div>
                        <span className="text-xs text-gray-500 block mb-1">Type de scan</span>
                        <span className="text-white capitalize">{formData.scan_type.replace('_', ' ')}</span>
                      </div>
                    </div>
                  </div>

                  {/* Network Mode */}
                  <div className="border-t border-gray-800 pt-4">
                    <h4 className="text-sm font-bold text-gray-400 mb-3">Mode réseau</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="text-xs text-gray-500 block mb-1">Mode</span>
                        <span className="text-white capitalize">
                          {networkModes.find(m => m.id === formData.network_mode)?.name || formData.network_mode}
                        </span>
                      </div>
                      {formData.network_mode === 'vpn_internal' && formData.vpn_internal_config_id && (
                        <div>
                          <span className="text-xs text-gray-500 block mb-1">Configuration VPN</span>
                          <span className="text-white">
                            {vpnInternalConfigs.find(c => c.id === formData.vpn_internal_config_id)?.name || 'Sélectionné'}
                          </span>
                        </div>
                      )}
                      {formData.network_mode === 'vpn_external' && formData.vpn_external_config_id && (
                        <div>
                          <span className="text-xs text-gray-500 block mb-1">Fournisseur VPN</span>
                          <span className="text-white">
                            {vpnExternalConfigs.find(c => c.id === formData.vpn_external_config_id)?.name || 'Sélectionné'}
                          </span>
                        </div>
                      )}
                      {formData.network_mode === 'proxy_pool' && formData.proxy_pool_id && (
                        <div>
                          <span className="text-xs text-gray-500 block mb-1">Pool de proxies</span>
                          <span className="text-white">
                            {proxyPools.find(p => p.id === formData.proxy_pool_id)?.name || 'Sélectionné'}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Scan Options */}
                  <div className="border-t border-gray-800 pt-4">
                    <h4 className="text-sm font-bold text-gray-400 mb-3">Options du scan</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <div>
                        <span className="text-xs text-gray-500 block mb-1">Ports</span>
                        <span className="text-white">{formData.ports === 'custom' ? formData.custom_ports : formData.ports === 'common' ? 'Courants' : formData.ports === 'all' ? 'Tous' : formData.ports}</span>
                      </div>
                      <div>
                        <span className="text-xs text-gray-500 block mb-1">Vitesse</span>
                        <span className="text-white capitalize">{formData.timing === 'paranoid' ? 'Paranoïaque' : formData.timing === 'sneaky' ? 'Furtif' : formData.timing === 'normal' ? 'Normal' : formData.timing === 'aggressive' ? 'Agressif' : formData.timing === 'insane' ? 'Extrême' : formData.timing}</span>
                      </div>
                      <div>
                        <span className="text-xs text-gray-500 block mb-1">Détection d'OS</span>
                        <span className={formData.os_detection ? 'text-brand-green' : 'text-gray-400'}>
                          {formData.os_detection ? 'Activé' : 'Désactivé'}
                        </span>
                      </div>
                      <div>
                        <span className="text-xs text-gray-500 block mb-1">Détection de services</span>
                        <span className={formData.service_detection ? 'text-brand-green' : 'text-gray-400'}>
                          {formData.service_detection ? 'Activé' : 'Désactivé'}
                        </span>
                      </div>
                      <div>
                        <span className="text-xs text-gray-500 block mb-1">Scan par scripts</span>
                        <span className={formData.script_scan ? 'text-brand-green' : 'text-gray-400'}>
                          {formData.script_scan ? 'Activé' : 'Désactivé'}
                        </span>
                      </div>
                      <div>
                        <span className="text-xs text-gray-500 block mb-1">Mode agressif</span>
                        <span className={formData.aggressive ? 'text-brand-green' : 'text-gray-400'}>
                          {formData.aggressive ? 'Activé' : 'Désactivé'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Advanced Options */}
                  <div className="border-t border-gray-800 pt-4">
                    <h4 className="text-sm font-bold text-gray-400 mb-3">Options avancées</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <div>
                        <span className="text-xs text-gray-500 block mb-1">{t('scans.stealthMode.title') || 'Mode furtif'}</span>
                        <span className={formData.stealth_mode !== 'none' ? 'text-brand-green' : 'text-gray-400'}>
                          {formData.stealth_mode === 'none' && (t('scans.stealthMode.none') || 'Aucun')}
                          {formData.stealth_mode === 'basic' && (t('scans.stealthMode.basic') || 'Basique')}
                          {formData.stealth_mode === 'advanced' && (t('scans.stealthMode.advanced') || 'Avancé')}
                          {formData.stealth_mode === 'paranoid' && (t('scans.stealthMode.paranoid') || 'Paranoïaque')}
                        </span>
                      </div>
                      <div>
                        <span className="text-xs text-gray-500 block mb-1">Tentatives max</span>
                        <span className="text-white">{formData.max_retries}</span>
                      </div>
                      <div>
                        <span className="text-xs text-gray-500 block mb-1">Délai d'expiration</span>
                        <span className="text-white">{formData.timeout}s</span>
                      </div>
                      {formData.exclude_hosts && (
                        <div className="col-span-2">
                          <span className="text-xs text-gray-500 block mb-1">Hôtes exclus</span>
                          <span className="text-white font-mono text-sm">{formData.exclude_hosts}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="text-blue-500 mt-0.5">ℹ️</div>
                  <div className="flex-1 text-xs text-gray-400">
                    <div className="font-medium text-blue-400 mb-1">Durée estimée</div>
                    <div>
                      {formData.scan_type === 'network_discovery' && 'Environ 5-15 minutes selon la taille du réseau'}
                      {formData.scan_type === 'vulnerability_scan' && 'Environ 30-60 minutes selon les cibles'}
                      {formData.scan_type === 'full_autonomous' && 'Environ 1-2 heures pour une évaluation complète'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Navigation Footer */}
        <div className="flex items-center justify-between bg-brand-black-light rounded-xl p-6 border border-brand-green/30">
          <button
            onClick={handleBack}
            disabled={currentStep === 1}
            className="px-6 py-3 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <ChevronLeft className="w-4 h-4" />
            {t('common.back')}
          </button>

          {currentStep < steps.length ? (
            <button
              onClick={handleNext}
              disabled={currentStep === 1 && !formData.target}
              className="btn-green disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {t('common.next')}
              <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <LicenseGatedButton
              onClick={handleSubmit}
              disabled={loading}
              feature="les scans"
              className="btn-green disabled:opacity-50 flex items-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-brand-black border-t-transparent rounded-full animate-spin"></div>
                  <span>{t('common.loading')}</span>
                </>
              ) : (
                <>
                  <Target className="w-5 h-5" />
                  <span>{t('scans.startScan')}</span>
                </>
              )}
            </LicenseGatedButton>
          )}
        </div>
      </div>
      </div>
    </div>
    </>
  );
};

export default NewScan;
